import React from 'react';
import styles from './index.module.scss';
import { safeJsonParse } from '../../../../utils/safeJsonParse';
import { formatContentHTML } from '../../../../utils/formatBoldText';
import { normalizeHref } from '../../../../utils/resolveSiteLink';

const normalizeText = (value) => {
  const text = String(value ?? '').trim();
  if (!text || text.toLowerCase() === 'none') return '';
  return text;
};

const getContentType = (item) =>
  String(item?.content_type || item?.cotent_type || '').trim().toLowerCase();

const getImageFileName = (item) => {
  const rawName = normalizeText(item?.img_name);
  if (!rawName) return '';
  if (rawName.includes('\\')) return rawName.substring(rawName.lastIndexOf('\\') + 1);
  if (rawName.includes('/')) return rawName.split('/').pop();
  return rawName;
};

const parsePublicationBlocks = (rawContent) => {
  if (!rawContent) return [];
  if (Array.isArray(rawContent)) return rawContent;
  if (typeof rawContent === 'object') return [rawContent];
  if (typeof rawContent === 'string') {
    const parsed = safeJsonParse(rawContent, null);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return [parsed];
  }
  return [];
};

const extractBlockContent = (contentBlocks) => {
  if (!Array.isArray(contentBlocks) || contentBlocks.length === 0) return [];

  const withBlockContent = contentBlocks.find((block) => Array.isArray(block?.block_content));
  if (withBlockContent?.block_content) return withBlockContent.block_content;

  return contentBlocks;
};

const parseLinkFromLine = (rawLine) => {
  const line = String(rawLine ?? '').trim();
  if (!line || line.toLowerCase() === 'none') {
    return { href: '', label: '' };
  }

  const startMarker = '=link';
  const endMarker = 'link=';

  if (line.includes(startMarker) && line.includes(endMarker)) {
    const startIndex = line.indexOf(startMarker);
    const afterStart = startIndex + startMarker.length;
    const endIndex = line.indexOf(endMarker, afterStart);

    if (endIndex !== -1) {
      const inner = line.substring(afterStart, endIndex).trim();
      const pipePos = inner.indexOf('|');
      let href = inner;
      let label = inner;

      if (pipePos !== -1) {
        href = inner.substring(0, pipePos).trim();
        label = inner.substring(pipePos + 1).trim() || href;
      }

      const normalizedHref = normalizeHref(href);
      if (normalizedHref) {
        return { href: normalizedHref, label };
      }

      return { href: '', label: '' };
    }
  }

  const normalizedHref = normalizeHref(line);
  return {
    href: normalizedHref,
    label: line,
  };
};

const parsePartnerText = (rawText) => {
  const lines = String(rawText ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '' && line.toLowerCase() !== 'none');

  const name = lines[0] || '';
  const description = lines[1] || '';
  const { href, label } = parseLinkFromLine(lines[2] || '');

  return { name, description, href, linkLabel: label };
};

const buildPartnersViewModel = (contentBlocks, pathToFolder) => {
  const blockContent = extractBlockContent(contentBlocks);
  if (!Array.isArray(blockContent) || blockContent.length === 0) {
    return { title: '', partners: [] };
  }

  let title = '';
  const firstItem = blockContent[0];
  if (getContentType(firstItem) === 'text') {
    title = normalizeText(firstItem?.content_text);
  }

  const partnerItems = blockContent.slice(getContentType(firstItem) === 'text' ? 1 : 0);
  const partners = partnerItems
    .filter((item) => getContentType(item) === 'img')
    .filter((item) => {
      const rawText = String(item?.content_text ?? '').trim();
      return rawText !== '' && rawText.toLowerCase() !== 'none';
    })
    .map((item) => {
      const fileName = getImageFileName(item);
      const { name, description, href, linkLabel } = parsePartnerText(item?.content_text);
      const logoUrl = fileName && pathToFolder ? `${pathToFolder}${fileName}` : '';

      return {
        id: fileName || name || linkLabel,
        logoUrl,
        name: name || normalizeText(item?.content_name || item?.cotent_name),
        description,
        href,
        linkLabel,
      };
    })
    .filter((partner) => partner.logoUrl || partner.name || partner.description || partner.href);

  return { title, partners };
};

const PartnerCard = ({ partner }) => (
  <article className={styles.partnerCard}>
    <div className={styles.partnerCardInner}>
      {partner.logoUrl && (
        <div className={styles.logoWrap}>
          <img className={styles.logo} src={partner.logoUrl} alt={partner.name || 'Партнёр'} loading='lazy' />
        </div>
      )}

      <div className={styles.info}>
        {partner.name && (
          <h3
            className={styles.name}
            dangerouslySetInnerHTML={{ __html: formatContentHTML(partner.name) }}
          />
        )}
        {partner.name && partner.description && <div className={styles.nameRule} aria-hidden='true' />}
        {partner.description && (
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{ __html: formatContentHTML(partner.description) }}
          />
        )}
        {partner.href && (
          <a
            href={partner.href}
            target='_blank'
            rel='noopener noreferrer'
            className={styles.partnerButton}>
            На сайт партнера
          </a>
        )}
      </div>
    </div>
  </article>
);

const Partners = ({ publication, globalIndex }) => {
  void globalIndex;

  const viewModel = React.useMemo(() => {
    const pathToFolder =
      publication?.path && publication?.eng_name
        ? `https://${publication.path}${publication.eng_name}/`
        : '';

    const contentBlocks = parsePublicationBlocks(publication?.content);
    return {
      pathToFolder,
      ...buildPartnersViewModel(contentBlocks, pathToFolder),
    };
  }, [publication]);

  if (!viewModel.pathToFolder || (viewModel.partners.length === 0 && !viewModel.title)) {
    return null;
  }

  return (
    <section className={styles.partners}>
      {viewModel.title && (
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{viewModel.title}</h2>
        </div>
      )}

      {viewModel.partners.length > 0 && (
        <div className={styles.partnersList}>
          {viewModel.partners.map((partner) => (
            <PartnerCard key={partner.id} partner={partner} />
          ))}
        </div>
      )}
    </section>
  );
};

export default Partners;
